import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import CreateTicket from "./pages/CreateTicket";
import TicketDetail from "./pages/TicketDetail";
import ServiceCatalog from "./pages/ServiceCatalog";
import TechMobile from "./pages/TechMobile";
import GrowthDashboard from "./pages/GrowthDashboard";
import WhiteLabelSettings from "./pages/WhiteLabelSettings";
import CyberDashboard from "./pages/CyberDashboard";
import ClientPortal from "./pages/ClientPortal";
import KnowledgeBase from "./pages/KnowledgeBase";
import MigrationDashboard from "./pages/MigrationDashboard";
import ScadaDashboard from "./pages/ScadaDashboard";
import RemoteSupport from "./pages/RemoteSupport";
import CannabisCompliance from "./pages/CannabisCompliance";
import StatusPage from "./pages/StatusPage";
import MetrcDashboard from "./pages/MetrcDashboard";
import SysmonDashboard from "./pages/SysmonDashboard";
import TrainHub from "./pages/TrainHub";
import UatChecklist from "./pages/UatChecklist";
import DocForge from "./pages/DocForge";
import StorePage from "./pages/StorePage";
import TalentHub from "./pages/TalentHub";
import ComplianceHub from "./pages/ComplianceHub";
import RemediationHub from "./pages/RemediationHub";
import WixHub from "./pages/WixHub";
import IntegrationHub from "./pages/IntegrationHub";
import HipaaHub from "./pages/HipaaHub";
import TechFinder from "./pages/TechFinder";
import PolicyCenter from "./pages/PolicyCenter";
import Pricing from "./pages/Pricing";
import Marketplace from "./pages/Marketplace";
import JoinNetwork from "./pages/JoinNetwork";
import DeviceDiagnostics from "./pages/DeviceDiagnostics";
import DiagnosticsSuccess from "./pages/DiagnosticsSuccess";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Legal from "./pages/Legal";
import CookieConsent from "./components/CookieConsent";
import { Toaster } from "./components/ui/sonner";
import ProductTour from "./components/ProductTour";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#002FA7]/20" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user || user === false) return <Navigate to="/login" replace />;
  return <>{children}<ProductTour /></>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user !== false) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously (before routes render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/diagnostics" element={<DeviceDiagnostics />} />
      <Route path="/diagnostics/success" element={<DiagnosticsSuccess />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/privacy" element={<Legal variant="privacy" />} />
      <Route path="/terms" element={<Legal variant="terms" />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/services" element={<ServiceCatalog />} />
      <Route path="/platform-status" element={<StatusPage />} />
      <Route path="/store" element={<StorePage />} />
      <Route path="/tech" element={<ProtectedRoute><TechMobile /></ProtectedRoute>} />
      <Route path="/growth" element={<ProtectedRoute><GrowthDashboard /></ProtectedRoute>} />
      <Route path="/whitelabel" element={<ProtectedRoute><WhiteLabelSettings /></ProtectedRoute>} />
      <Route path="/cyber" element={<ProtectedRoute><CyberDashboard /></ProtectedRoute>} />
      <Route path="/portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
      <Route path="/kb" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
      <Route path="/migration" element={<ProtectedRoute><MigrationDashboard /></ProtectedRoute>} />
      <Route path="/scada" element={<ProtectedRoute><ScadaDashboard /></ProtectedRoute>} />
      <Route path="/remote" element={<ProtectedRoute><RemoteSupport /></ProtectedRoute>} />
      <Route path="/cannabis" element={<ProtectedRoute><CannabisCompliance /></ProtectedRoute>} />
      <Route path="/metrc" element={<ProtectedRoute><MetrcDashboard /></ProtectedRoute>} />
      <Route path="/sysmon" element={<ProtectedRoute><SysmonDashboard /></ProtectedRoute>} />
      <Route path="/train" element={<ProtectedRoute><TrainHub /></ProtectedRoute>} />
      <Route path="/uat" element={<ProtectedRoute><UatChecklist /></ProtectedRoute>} />
      <Route path="/docforge" element={<ProtectedRoute><DocForge /></ProtectedRoute>} />
      <Route path="/talent" element={<ProtectedRoute><TalentHub /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><ComplianceHub /></ProtectedRoute>} />
      <Route path="/remediation" element={<ProtectedRoute><RemediationHub /></ProtectedRoute>} />
      <Route path="/wixhub" element={<ProtectedRoute><WixHub /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><IntegrationHub /></ProtectedRoute>} />
      <Route path="/hipaa" element={<ProtectedRoute><HipaaHub /></ProtectedRoute>} />
      <Route path="/techfinder" element={<ProtectedRoute><TechFinder /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute><PolicyCenter /></ProtectedRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/join" element={<JoinNetwork />} />
      <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
      <Route path="/tickets/new" element={<ProtectedRoute><CreateTicket /></ProtectedRoute>} />
      <Route path="/tickets/:id" element={<ProtectedRoute><TicketDetail /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieConsent />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
